/**
 * JMC settings.
 *
 * Three settings, in descending order of what they cost if wrong: the model
 * every generation runs on and what it is billed at, the plans every clinic is
 * measured against, and JMC's own monthly ceiling above both.
 *
 * Plans live here rather than in a screen of their own because they are exactly
 * that kind of setting - JMC writes a number once, and every clinic's dashboard,
 * quota refusal and invoice is downstream of it. Nothing about them is compiled
 * in: db/seed.ts writes three to start with and never touches them again.
 *
 * The demo-data panel is gone. It was a way to see populated screens before
 * there was anything real to look at, and this installation has real clinics,
 * real usage and a real corpus - a button that inserts 「【デモ】」 tenants into
 * that is a way to corrupt the figures, not a way to check them. The endpoints
 * remain for development and are still covered by the security tests; what is
 * removed is the invitation to press it in production.
 */
import { useState } from 'react';
import { useLoaderData, useRevalidator, useRouteLoaderData } from 'react-router';
import { ModelSettings, type AiModelList } from '../components/ModelSettings.tsx';
import { PlanSettings } from '../components/PlanSettings.tsx';
import { ApiError, patch, type AdminPlan, type ListResponse, type Session } from '../api.ts';
import { ThemePicker } from '../components/ThemePicker.tsx';
import { Card, Stat, jaNumber } from '../components/ui.tsx';
import { InfoButton } from '../components/InfoButton.tsx';

export interface PlatformSettings {
  /** Monthly vendor AI budget in micro-USD. null when JMC has not set one. */
  aiBudgetMicro: number | null;
}

/**
 * What JMC is prepared to spend with the model vendor in a month.
 *
 * Entered in dollars because that is the unit of the invoice JMC pays, stored
 * in micro-USD because that is the unit the ledger counts in and money never
 * becomes a float on the way. Blank clears it, and cleared is a real state: the
 * dashboard then shows the spend on its own rather than a percentage of a
 * number nobody chose.
 */
function AiBudget({ settings, plans }: { settings: PlatformSettings; plans: AdminPlan[] }) {
  const revalidator = useRevalidator();
  const initial = settings.aiBudgetMicro === null ? '' : String(settings.aiBudgetMicro / 1_000_000);
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = value.trim() !== initial;

  async function save() {
    const text = value.trim();
    const dollars = text === '' ? null : Number(text);
    if (dollars !== null && (!Number.isFinite(dollars) || dollars <= 0)) {
      setError('0より大きい金額を入力してください。');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await patch('/admin/platform-settings', {
        aiBudgetMicro: dollars === null ? null : Math.round(dollars * 1_000_000),
      });
      await revalidator.revalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存できませんでした');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="AI利用の月間予算"
      caption="JMCがAIベンダーへ支払う金額の上限です。クリニックには表示されません。"
      actions={
        <div className="chips">
          <InfoButton title="月間予算について">
            <p>
              クリニックに<strong>売っている枠</strong>（契約プラン）と、JMCが
              <strong>支払う上限</strong>は別物です。ここはあとの方です。
            </p>
            <p className="small">
              ダッシュボードの利用額タイルは、この金額に対する割合として表示されます。
              未設定なら割合は出さず、金額だけを表示します。
              すべてのクリニックがプランを使い切った場合の合計額を上限にしていないのは、
              それが誰も決めていない数字だからです。
            </p>
            <p className="small">
              この金額を超えても生成は止まりません。止まるのは各クリニックの
              プラン枠を超えたときです。ここはJMC自身の監視のための数字です。
            </p>
          </InfoButton>
          <button
            className="btn btn--sm"
            type="button"
            onClick={() => void save()}
            disabled={busy || !dirty}
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      }
    >
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="filters">
        <div className="field" style={{ marginBottom: 0, maxWidth: 220 }}>
          <label htmlFor="ai-budget">月間予算（USD）</label>
          <input
            id="ai-budget"
            type="number"
            min="1"
            step="1"
            inputMode="decimal"
            placeholder="未設定"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <span className="field__hint">空欄にして保存すると未設定に戻ります。</span>
        </div>
      </div>

      {/*
        The two numbers beside each other, because the question this setting
        raises is "how does my ceiling compare with what I have sold" - and that
        is the sum of the plans, which is on this same screen.
      */}
      <div className="grid grid--2">
        <Stat
          label="設定中の月間予算"
          value={settings.aiBudgetMicro === null ? '未設定' : `$${(settings.aiBudgetMicro / 1_000_000).toFixed(0)}`}
          small
        />
        <Stat
          label="販売中のプラン"
          value={jaNumber(plans.filter((plan) => plan.is_active).length)}
          hint={<span className="delta">停止中を含めて {jaNumber(plans.length)} 件</span>}
          small
        />
      </div>
    </Card>
  );
}

export default function AdminSettings() {
  const { models, plans, platform } = useLoaderData<{
    models: AiModelList;
    plans: ListResponse<AdminPlan>;
    platform: PlatformSettings;
  }>();
  const revalidator = useRevalidator();
  const session = useRouteLoaderData('root') as Session;

  return (
    <>
      {/*
        Which model every clinic's generation uses, and what it is billed at.
        First on the screen because it is the setting with a bill attached.
      */}
      <ModelSettings data={models} onChanged={() => void revalidator.revalidate()} />

      {/*
        The plans every clinic is measured against. Second, because it is the
        other setting that decides what a tenant may spend - and because the
        numbers on a clinic's dashboard mean nothing until these exist.
      */}
      <PlanSettings plans={plans} />

      {/*
        JMC's own ceiling, below the plans it sells: the plans decide what a
        clinic may spend, this decides what JMC is willing to be billed in total.
      */}
      <AiBudget settings={platform} plans={plans.items} />

      {/*
        The operator's OWN colour for the JMC workspace - not a clinic's. Each
        clinic's is set on its page; this one belongs to whoever is signed in.
      */}
      <ThemePicker
        endpoint="/auth/me/theme"
        value={session.theme?.brandColor ?? null}
        caption="あなた自身のJMC管理画面の色です。ほかのJMC管理者やクリニックの画面には影響しません。クリニックの色は各クリニックのページで設定します。"
      />
    </>
  );
}
